import i18n from 'i18n';
import { isListEnabled } from 'utils/queryHelpers';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { NotificationType, QueryContextFromKeys, QueryParamModel } from 'models';
import { statisticsService, timeLogService } from 'services';
import { showNotification } from 'components';
import { isAxiosError } from 'utils/errors';
import { useAsyncExcelExport } from 'features/asyncExport';

type TimeLogQueryContexts = QueryContextFromKeys<typeof timeLogKeys>;

export const timeLogKeys = {
  all: [{ scope: 'time-log' }] as const,
  lists: () => [{ ...timeLogKeys.all[0], entity: 'list' }] as const,
  timeLogList: (params?: QueryParamModel | null) =>
    [{ ...timeLogKeys.lists()[0], params }] as const,
};

const fetchTimeLogList = async ({ queryKey: [{ params }] }: TimeLogQueryContexts['timeLogList']) =>
  statisticsService.getTimeLogList(params);

const exportTimeLogList = async ({ queryKey: [{ params }] }: TimeLogQueryContexts['timeLogList']) =>
  statisticsService.exportTimeLogList(params);

export const useTimeLogList = (params?: QueryParamModel | null) => {
  return useQuery({
    queryKey: timeLogKeys.timeLogList(params),
    queryFn: fetchTimeLogList,
    enabled: isListEnabled(params),
  });
};

export const useExportTimeLogList = (params?: QueryParamModel | null) => {
  return useAsyncExcelExport(timeLogKeys.timeLogList(params), exportTimeLogList);
};

export const useImportTimeLog = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => timeLogService.upload(data.file, data.date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeLogKeys.lists() });
    },
    onError: (e) => {
      if (isAxiosError(e) && (e.response?.status === 400 || e.response?.status === 404)) {
        showNotification({
          content: i18n.t('ERRORS.TIME_LOG_INVALID_FILE'),
          type: NotificationType.ERROR,
        });
      }
    },
  });
};
